import { emptyApi } from "./emptyApi";

export type BusinessReviewStatus = 0 | 1; // 0=DRAFT, 1=SENT

export type BusinessReview = {
  uuid: string;
  title: string;
  entity_id: string;
  entity_name: string;
  start_date: string;
  end_date: string;
  comparison_start_date: string | null;
  comparison_end_date: string | null;
  status: BusinessReviewStatus;
  sent_at: string | null;
  created_at: string;
  created_by: string | null;
  snapshot?: object;
};

export type ReviewTheme = "dark" | "light" | "ocean";

export type CreateBusinessReviewPayload = {
  start_date: string;
  end_date: string;
  comparison_start_date?: string | null;
  comparison_end_date?: string | null;
  theme?: ReviewTheme;
};

const BASE = "/api/inkle/dashboards/v1";

export const businessReviewApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    listBusinessReviews: build.query<BusinessReview[], { entityId: string }>({
      query: ({ entityId }) => ({
        url: `${BASE}/entity/${entityId}/business-reviews/`,
      }),
      providesTags: ["BUSINESS_REVIEWS"],
    }),

    createBusinessReview: build.mutation<
      BusinessReview,
      { entityId: string } & CreateBusinessReviewPayload
    >({
      query: ({ entityId, ...body }) => ({
        url: `${BASE}/entity/${entityId}/business-reviews/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BUSINESS_REVIEWS"],
    }),

    getBusinessReview: build.query<BusinessReview, { reviewId: string }>({
      query: ({ reviewId }) => ({
        url: `${BASE}/business-reviews/${reviewId}/`,
      }),
      providesTags: (_res, _err, { reviewId }) => [
        { type: "BUSINESS_REVIEWS", id: reviewId },
      ],
    }),

    saveBusinessReview: build.mutation<
      BusinessReview,
      { reviewId: string; snapshot: object }
    >({
      query: ({ reviewId, snapshot }) => ({
        url: `${BASE}/business-reviews/${reviewId}/`,
        method: "PUT",
        body: { snapshot },
      }),
      invalidatesTags: (_res, _err, { reviewId }) => [
        { type: "BUSINESS_REVIEWS", id: reviewId },
      ],
    }),

    deleteBusinessReview: build.mutation<void, { reviewId: string }>({
      query: ({ reviewId }) => ({
        url: `${BASE}/business-reviews/${reviewId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["BUSINESS_REVIEWS"],
    }),

    sendBusinessReview: build.mutation<BusinessReview, { reviewId: string }>({
      query: ({ reviewId }) => ({
        url: `${BASE}/business-reviews/${reviewId}/send/`,
        method: "POST",
      }),
      invalidatesTags: ["BUSINESS_REVIEWS"],
    }),

    exportBusinessReview: build.mutation<
      { document_id: string },
      { entityId: string; reviewId: string; graphs: string[] }
    >({
      query: ({ entityId, reviewId, graphs }) => ({
        url: `${BASE}/entity/${entityId}/business-reviews/${reviewId}/export_graphs/`,
        method: "POST",
        body: { graphs },
      }),
    }),
  }),
});

export const {
  useListBusinessReviewsQuery,
  useCreateBusinessReviewMutation,
  useGetBusinessReviewQuery,
  useSaveBusinessReviewMutation,
  useDeleteBusinessReviewMutation,
  useSendBusinessReviewMutation,
  useExportBusinessReviewMutation,
} = businessReviewApi;
